import React, { useEffect, useState } from 'react';
import { fetchCustomers, getCustomerByEmail } from '../services/customerService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/PersonRemoveOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { Business as CustomerIcon } from '@mui/icons-material';
import { useOrganization } from '../contexts/OrganizationContext';
import { updateSubscriptionFields, removeCustomerFromSubscription } from '../services/subscriptionService';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

const Customers = () => {
  const { selectedOrg, updateSelectedOrg } = useOrganization();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  const navigate = useNavigate();

  const loadCustomers = async () => {
    if (!selectedOrg) {
      setError('No organization selected.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchCustomers(selectedOrg);
      setCustomers(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch customer data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [selectedOrg]);

  const handleAddCustomer = () => {
    setOpenDialog(true); // Open the dialog box
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setExistingEmail('');
  };

  const handleCreateNewCustomer = () => {
    setOpenDialog(false);
    navigate('/customers/new'); // Navigate to the customer creation form
  };

  const handleAddExistingCustomer = async () => {
    if (!existingEmail) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      const customer = await getCustomerByEmail(existingEmail);

      if (customer) {
        // Add the customer to the subscription
        await updateSubscriptionFields(selectedOrg, { assignedCustomers: [customer.customerGuid] }, 'add');
        await loadCustomers(); // Reload customers to update the UI
      } else {
        alert('Customer not found.');
      }
    } catch (err) {
      console.error('Error adding existing customer:', err);
      alert('Failed to add customer. Please try again.');
    } finally {
      handleDialogClose();
    }
  };

  const handleEditCustomer = (customerGuid) => {
    navigate(`/customers/${customerGuid}`); // Navigate to customer details page for editing
  };

  const handleDeleteCustomer = async (customerGuid) => {
    if (window.confirm('Are you sure you want to remove this customer?')) {
      setCustomers(customers.filter((customer) => customer.customerGuid !== customerGuid));
      // Make API call to delete customer
      try {
        await removeCustomerFromSubscription(selectedOrg, customerGuid);
        alert('Customer removed successfully.');
        await loadCustomers(selectedOrg);
      } catch (error) {
        alert('Failed to remove customer.');
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            color: '#2c3e50',
            fontWeight: 'bold',
          }}
        >
          Loading Customers...
        </Typography>
      </Box>
    );
  }

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 0, backgroundColor: '#f9f9f9' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        {/* Left-aligned content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CustomerIcon sx={{ fontSize: '24px', color: '#2c3e50' }} />
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: '#2c3e50' }}
          >
            Customers
          </Typography>
        </Box>

        {/* Right-aligned button */}
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddCustomer}
        >
          Add Customer
        </Button>
      </Box>

      {/* Dialog for Add Customer */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Add Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to create a new customer or add an existing customer by email?
          </DialogContentText>
          <Box sx={{ marginTop: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCreateNewCustomer}
              sx={{ marginBottom: 2 }}
            >
              Create New Customer
            </Button>
            <TextField
              label="Enter Existing Customer Email"
              fullWidth
              value={existingEmail}
              onChange={(e) => setExistingEmail(e.target.value)}
              sx={{ marginBottom: 2 }}
            />
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleAddExistingCustomer}
            >
              Add Existing Customer
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Name
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Contact
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Email
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Phone Number
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Account Created
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer) => (
            <TableRow key={customer.customerGuid}>
              <TableCell sx={{ padding: '4px 8px' }}>{customer.businessName}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{customer.contactName}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{customer.email}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{customer.phoneNumber || 'N/A'}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>
                {new Date(customer.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>
                <Tooltip title="Edit">
                  <IconButton
                    color="primary"
                    onClick={() => handleEditCustomer(customer.customerGuid)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteCustomer(customer.customerGuid)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default Customers;